import { track, analyticsKeys } from '@mobi/account/Utils'
import { DepositLimitFrequency } from '@mobi/api-types'

type PersonalDetailsUpdateOption =
  | 'Email'
  | 'Mobile Number'
  | 'Residential Address'
  | 'Postal Address'
  | 'Combined Address'
type Location =
  | 'Bet Limits'
  | 'Deposit Limit'
  | 'Deposit Limit Pop Up'
  | 'Personal Details Update'
  | 'ID Verification Home'

export type AccountManagementEvent = {
  interaction_type:
    | 'Set Deposit Limit'
    | 'Edit Deposit Limit'
    | 'Deposit Limit Pop Up Displayed'
    | 'Keep Deposit Limit'
    | 'Remove Bet Limit'
    | 'Set Bet Limit'
    | 'Need Assistance'
    | 'Block Debit Card'
    | 'Unblock Debit Card'
    | 'Cancel Unblock Debit Card'
    | 'Bet Limit Error'
    | 'Deposit Limit Error'
    | 'Personal Details Update Success'
    | 'Personal Details Update Error'
  limitAmount: string | undefined
  location: Location | undefined
  depositFrequency: DepositLimitFrequency | 'None' | undefined
  message: string | undefined
  optionSelected: PersonalDetailsUpdateOption | undefined
}

const defaultAccountManagementValues: Omit<AccountManagementEvent, 'interaction_type'> = {
  limitAmount: undefined,
  location: undefined,
  depositFrequency: undefined,
  message: undefined,
  optionSelected: undefined,
}

export const accountManagementTracker = (event: AccountManagementEvent) =>
  track(analyticsKeys.accountManagement, event)

export const trackNeedAssistance = (location: Location) =>
  accountManagementTracker({
    interaction_type: 'Need Assistance',
    ...defaultAccountManagementValues,
    location,
  })

/*
 * Bet Limit
 */

export const trackRemoveBetLimit = () =>
  accountManagementTracker({
    interaction_type: 'Remove Bet Limit',
    ...defaultAccountManagementValues,
  })

export const trackSetBetLimit = (limitAmount: string) =>
  accountManagementTracker({
    interaction_type: 'Set Bet Limit',
    ...defaultAccountManagementValues,
    limitAmount,
  })

export const trackBetLimitError = (message: string) =>
  accountManagementTracker({
    interaction_type: 'Bet Limit Error',
    ...defaultAccountManagementValues,
    location: 'Bet Limits',
    message: message,
  })

/*
 * Deposit Limit
 */

export const trackSetDepositLimit = (
  depositFrequency: DepositLimitFrequency | undefined,
  limitAmount: string | undefined
) =>
  accountManagementTracker({
    interaction_type: 'Set Deposit Limit',
    ...defaultAccountManagementValues,
    depositFrequency: depositFrequency ?? 'None',
    limitAmount,
  })

export const trackEditDepositLimit = () =>
  accountManagementTracker({
    interaction_type: 'Edit Deposit Limit',
    ...defaultAccountManagementValues,
    location: 'Deposit Limit Pop Up',
  })

export const trackDepositLimitPopUpDisplayed = () =>
  accountManagementTracker({
    interaction_type: 'Deposit Limit Pop Up Displayed',
    ...defaultAccountManagementValues,
  })

export const trackKeepDepositLimit = () =>
  accountManagementTracker({
    interaction_type: 'Keep Deposit Limit',
    ...defaultAccountManagementValues,
    location: 'Deposit Limit Pop Up',
  })

export const trackDepositLimitError = (message: string) =>
  accountManagementTracker({
    interaction_type: 'Deposit Limit Error',
    ...defaultAccountManagementValues,
    location: 'Deposit Limit',
    message: message,
  })

/*
 * Block Debit Card
 */

export const trackBlockDebitCard = () =>
  accountManagementTracker({
    interaction_type: 'Block Debit Card',
    ...defaultAccountManagementValues,
  })

export const trackUnblockDebitCard = () =>
  accountManagementTracker({
    interaction_type: 'Unblock Debit Card',
    ...defaultAccountManagementValues,
  })

export const trackCancelUnblockDebitCard = () =>
  accountManagementTracker({
    interaction_type: 'Cancel Unblock Debit Card',
    ...defaultAccountManagementValues,
  })

/*
 * Personal Details
 */

export const trackPersonalDetailsUpdateSuccess = (optionSelected: PersonalDetailsUpdateOption) =>
  accountManagementTracker({
    interaction_type: 'Personal Details Update Success',
    ...defaultAccountManagementValues,
    optionSelected,
  })

export const trackPersonalDetailsUpdateError = (message: string) =>
  accountManagementTracker({
    interaction_type: 'Personal Details Update Error',
    ...defaultAccountManagementValues,
    message,
  })
